// RFC-0944 — project-scoped rules modal.
//
// Opened from a dedicated button in the floating canvas toolbar
// (CanvasToolbox). Mirrors UserRulesModal: wraps the shared ui/Modal
// (size="lg") around the hook-driven loading/error states + the scope-generic
// <RulesEditor scope="project">. Two behaviors the shared Modal deliberately
// does NOT provide (so we don't change it globally): (1) initial focus into the
// body textarea on open, handled by RulesEditor's `autoFocusBody`; (2) an
// unsaved-changes close guard so an accidental ESC / backdrop / X in the
// textarea doesn't silently discard edits.
//
// projectId comes from useProjectId() (the modal renders under
// <ProjectIdProvider> in WorkspacePageV2). Non-owners get a read-only editor +
// a permission notice, driven by useProjectRules' owner-gated `canWrite`.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

import Modal from '../ui/Modal'
import RulesEditor from './RulesEditor'
import { useProjectRules } from '../../hooks/useProjectRules'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { createNadDebug } from '../../utils/debugLog'
import { trackInteraction } from '../../analytics/interactionAnalytics'

const log = createNadDebug('dw:rules-modal')

interface ProjectRulesModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function ProjectRulesModal({
  isOpen,
  onClose,
}: ProjectRulesModalProps): JSX.Element {
  const { t } = useTranslation('pages')
  const projectId = useProjectId()
  const { doc, version, history, canWrite, loading, loadError, save } =
    useProjectRules(projectId, isOpen)
  const [dirty, setDirty] = useState(false)
  const [discardPrompt, setDiscardPrompt] = useState(false)
  const discardCancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setDirty(false)
    setDiscardPrompt(false)
    log({
      event: 'modal:open',
      trace_id: `rules-modal:project:${projectId}`,
      modal: 'project-rules',
      project_id: projectId,
    })
  }, [isOpen, projectId])

  const closeAndLog = useCallback(() => {
    log({
      event: 'modal:close',
      trace_id: `rules-modal:project:${projectId}`,
      modal: 'project-rules',
      project_id: projectId,
      dirty,
    })
    onClose()
  }, [dirty, onClose, projectId])

  // Guarded close: when the editor has unsaved edits, surface an in-app discard
  // confirmation (mirroring the node-delete dialog + the user-rules fix #2422)
  // instead of the jarring browser-native window.confirm(). Wired to
  // ESC + backdrop + X via Modal's single onClose.
  const guardedClose = useCallback(() => {
    if (dirty) {
      setDiscardPrompt(true)
      return
    }
    closeAndLog()
  }, [dirty, closeAndLog])

  const confirmDiscard = useCallback(() => {
    trackInteraction('settings_rules_action', { action: 'discard', scope: 'project' })
    setDiscardPrompt(false)
    closeAndLog()
  }, [closeAndLog])

  const cancelDiscard = useCallback(() => setDiscardPrompt(false), [])

  // A11y: when the discard prompt opens, move focus to its safe default action
  // ("Keep editing") so keyboard users land on a non-destructive control and
  // can Tab to "Discard changes". The shared Modal doesn't auto-focus content.
  // (Mirrors the sibling user-rules fix #2422 / its CodeRabbit a11y finding.)
  useEffect(() => {
    if (discardPrompt) discardCancelRef.current?.focus()
  }, [discardPrompt])

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={guardedClose}
      title={t('rules.projectModalTitle')}
      size="lg"
    >
      <div data-testid="project-rules-modal">
        {loading ? (
          <div
            className="flex items-center justify-center py-12 text-sm text-muted-foreground"
            data-testid="project-rules-modal-loading"
          >
            {t('rules.projectLoading')}
          </div>
        ) : loadError !== null ? (
          <div
            className="py-8 text-center text-sm text-red-400"
            data-testid="project-rules-modal-error"
          >
            {t('rules.loadFailed', { message: loadError })}
          </div>
        ) : (
          <>
            {!canWrite ? (
              <div
                className="mb-4 rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-xs font-medium text-muted-foreground"
                data-testid="project-rules-modal-readonly-notice"
              >
                {t('rules.projectReadOnlyNotice')}
              </div>
            ) : null}
            <RulesEditor
              scope="project"
              initialDocument={doc}
              initialVersion={version}
              history={history}
              onSave={save}
              isReadOnly={!canWrite}
              autoFocusBody
              onDirtyChange={setDirty}
            />
          </>
        )}
      </div>
    </Modal>

      <Modal
        isOpen={isOpen && discardPrompt}
        onClose={cancelDiscard}
        title={t('rules.discardTitle')}
        size="sm"
      >
        <div data-testid="project-rules-discard-dialog" className="space-y-4">
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <p className="min-w-0 text-sm text-foreground">{t('rules.projectDiscardConfirm')}</p>
          </div>

          <div className="flex justify-end gap-2">
            <button
              ref={discardCancelRef}
              type="button"
              data-testid="project-rules-discard-cancel"
              onClick={cancelDiscard}
              className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1"
            >
              {t('rules.discardKeepEditing')}
            </button>
            <button
              type="button"
              data-testid="project-rules-discard-confirm"
              onClick={confirmDiscard}
              className="h-9 rounded-lg bg-destructive px-3 text-sm font-medium text-white hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1"
            >
              {t('rules.discardConfirmCta')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
